const NON_DIGITS_REGEX = /\D/g

export const DietaryMaterialList = (json: IDietaryMaterialList): IDietaryMaterialListModel => {
	const {
		id,
		name,
		internal_name,
		warehouse_name,
		food_category,
		is_active,
		is_liquid,
		thermal_processing_coefficient,
		mechanical_processing_coefficient,
		supplier_name,
		allergens,
		nutritional_parameters,
		created_at,
		updated_at,
		...rest
	} = json

	return {
		...rest,
		id,
		name: LangContentToModel(name),
		internalName: internal_name,
		warehouseName: warehouse_name,
		foodCategory: food_category,
		isActive: is_active,
		isLiquid: is_liquid,
		thermalProcessingCoefficient: thermal_processing_coefficient,
		mechanicalProcessingCoefficient: mechanical_processing_coefficient,
		supplierName: supplier_name ?? null,
		allergens: allergens ? allergens.map(DietAllergen) : [],
		nutritionalParameters: nutritional_parameters ? nutritional_parameters.map(NutritionalParameterWithValue) : [],
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
	}
}

export const DietaryMaterial = (json: IDietaryMaterial): IDietMaterialModel => {
	const { is_monoproduct, alternatives, dietary_tags, external_id, mapi_piece_weight, unit, ...rest } = json

	return {
		...DietaryMaterialList(rest),
		unit,
		isMonoproduct: is_monoproduct,
		dietaryTags: dietary_tags ? dietary_tags.map(DietDietaryTag) : [],
		alternatives: alternatives ? alternatives.map(DietaryRawMaterialAlternativeToModel) : [],
		externalId: external_id ?? null,
		mapiPieceWeight: mapi_piece_weight,
	}
}

/**
 * Related meals / semi-products
 */
const DietaryRawMaterialRelatedMealVariant = (
	json: IDietaryRawMaterialRelatedMealVariant,
): IDietaryRawMaterialRelatedMealVariantModel => {
	const { id, is_active, margin, cost_gross, meal_size_id, meal_size_name } = json

	return {
		id,
		margin,
		isActive: is_active,
		costGross: cost_gross,
		mealSizeId: meal_size_id,
		mealSizeName: meal_size_name,
	}
}

export const DietaryRawMaterialRelatedMeal = (
	json: IDietaryRawMaterialRelatedMeal,
): IDietaryRawMaterialRelatedMealModel => {
	const {
		id,
		name,
		internal_name,
		meal_type,
		average_margin,
		average_cost_gross,
		dietary_tags,
		allergens,
		variants,
	} = json

	return {
		id,
		name: LangContentToModel(name),
		internalName: internal_name,
		mealType: meal_type,
		averageMargin: average_margin,
		averageCostGross: average_cost_gross,
		dietaryTags: dietary_tags ? dietary_tags.map(DietDietaryTag) : [],
		allergens: allergens ? allergens.map(DietAllergen) : [],
		variants: variants ? variants.map(DietaryRawMaterialRelatedMealVariant) : [],
	}
}

export const DietaryRawMaterialRelatedSemiProduct = (
	json: IDietaryRawMaterialRelatedSemiProduct,
): IDietaryRawMaterialRelatedSemiProductModel => {
	const { id, name, internal_name, warehouse_name, group_id, group_name, is_alternative, allergens } = json

	return {
		id,
		name: LangContentToModel(name),
		internalName: internal_name,
		warehouseName: warehouse_name,
		groupId: group_id,
		groupName: group_name,
		isAlternative: is_alternative,
		allergens: allergens ? allergens.map(DietAllergen) : [],
	}
}

export const DietaryRawMaterialRelated = (json: IDietaryRawMaterialRelated): IDietaryRawMaterialRelatedModel => {
	const { meals, semi_products } = json

	return {
		meals: meals ? meals.map(DietaryRawMaterialRelatedMeal) : [],
		semiProducts: semi_products ? semi_products.map(DietaryRawMaterialRelatedSemiProduct) : [],
	}
}

const DietMaterialBaseToPayload = (
	model: IDietaryRawMaterialForm,
	options: IDietMaterialOptions,
): IDietMaterialBasePayload => {
	const {
		internalName,
		warehouseName,
		foodCategory,
		unit,
		ean,
		externalId,
		mapiPieceWeight,
		isLiquid,
		isMonoproduct,
		supplierName,
		thermalProcessingCoefficient,
		mechanicalProcessingCoefficient,
		ingredients,
		...rest
	} = model

	const { allergens, dietaryTags, isIngredientsManual } = options

	const nutritional_values: Array<INutritionalParameterWithValueModel> = []
	Object.entries(rest).forEach(([name, _value]) => {
		if (name.startsWith('nutritionalParameter')) {
			const id = Number(name.replace(NON_DIGITS_REGEX, ''))

			let value: number | null | undefined
			if (_value === null) {
				value = undefined
			} else {
				let parsedValue = _value
				if (typeof parsedValue === 'string' && parsedValue.includes(',')) {
					parsedValue = parsedValue.replace(',', '.')
				}
				const numValue = Number(parsedValue)
				value = Number.isNaN(numValue) ? null : numValue
			}

			if (value === null || value === undefined) return

			nutritional_values.push({ id, value })
		}
	})

	return {
		unit,
		ean,
		nutritional_values,
		internal_name: internalName,
		warehouse_name: warehouseName,
		food_category: foodCategory,
		thermal_processing_coefficient: thermalProcessingCoefficient,
		mechanical_processing_coefficient: mechanicalProcessingCoefficient,
		ingredients: isIngredientsManual ? ingredients : null,
		is_active: true,
		external_id: externalId,
		mapi_piece_weight: mapiPieceWeight,
		is_liquid: isLiquid,
		is_monoproduct: isMonoproduct,
		supplier_name: supplierName ?? null,
		allergens: allergens ? allergens.map(allergen => allergen.value) : [],
		dietary_tags: dietaryTags ? dietaryTags.map(tag => tag.value) : [],
	}
}

export const DietMaterialToCreatePayload = (
	model: IDietaryRawMaterialForm,
	options: IDietMaterialOptions,
): IDietMaterialCreatePayload => {
	const { name, nameEn, nameUa } = model

	const payload = DietMaterialBaseToPayload(model, options)

	const names = {
		pl: name,
		en: nameEn,
		ua: nameUa,
	}

	const nameArray = generateLangContentArrayFromObject(names)

	return {
		name: nameArray,
		...payload,
	}
}

export const DietMaterialToUpdatePayload = (
	base: IDietMaterialModel,
	model: IDietaryRawMaterialForm,
	options: IDietMaterialOptions,
): IDietMaterialUpdatePayload => {
	const { id } = base

	const payload = DietMaterialBaseToPayload(model, options)

	const names = {
		pl: model.name ?? base.name.pl,
		en: model.nameEn ?? base.name.en,
		ua: model.nameUa ?? base.name.ua,
	}

	const nameArray = generateLangContentArrayFromObject(names)

	return {
		id,
		name: nameArray,
		...payload,
	}
}
